import React, { useState } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';

const RequestMoney: React.FC = () => {
  const colors = getThemeColors('dark');

  const [amount, setAmount] = useState('');
  const [recipient, setRecipient] = useState('');
  const [requestType, setRequestType] = useState('phone');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const requestTypes = [
    { id: 'phone', name: 'Phone Number', icon: '📱', placeholder: 'Enter phone number' },
    { id: 'email', name: 'Email', icon: '📧', placeholder: 'Enter email address' },
    { id: 'user', name: 'Username', icon: '👤', placeholder: 'Enter username' },
  ];

  const handleRequest = async () => {
    const requestAmount = parseFloat(amount) || 0;

    if (!requestAmount || requestAmount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (!recipient.trim()) {
      alert('Please enter a recipient');
      return;
    }

    try {
      setLoading(true);
      // Mock API call - in real app this would call actual API
      await new Promise(resolve => setTimeout(resolve, 2000));

      alert(`Money request of KES ${requestAmount.toLocaleString()} sent to ${recipient} successfully`);
      setAmount('');
      setRecipient('');
      setReason('');
    } catch (error) {
      console.error('Request failed:', error);
      alert('Request failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: spacing.md,
    },
    header: {
      textAlign: 'center',
      marginBottom: spacing.xl,
    },
    title: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    subtitle: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    form: {
      maxWidth: '600px',
      margin: '0 auto',
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.xl,
      ...getShadowStyle('md'),
    },
    section: {
      marginBottom: spacing.xl,
    },
    sectionTitle: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.semibold,
      color: colors.text,
      marginBottom: spacing.md,
    },
    label: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      color: colors.text,
      marginBottom: spacing.sm,
      display: 'block',
    },
    amountInput: {
      width: '100%',
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      textAlign: 'center',
      border: `2px solid ${colors.border}`,
      backgroundColor: colors.surface,
      color: colors.text,
      outline: 'none',
      boxSizing: 'border-box',
    },
    methodSelector: {
      display: 'flex',
      gap: spacing.sm,
      marginBottom: spacing.md,
    },
    methodButton: {
      flex: 1,
      padding: spacing.md,
      borderRadius: borderRadius.lg,
      border: `2px solid ${colors.border}`,
      backgroundColor: colors.surface,
      color: colors.text,
      cursor: 'pointer',
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      transition: 'all 0.2s ease',
    },
    activeMethod: {
      borderColor: colors.success,
      backgroundColor: colors.success + '20',
      color: colors.success,
    },
    input: {
      width: '100%',
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      fontSize: typography.fontSize.base,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
      color: colors.text,
      outline: 'none',
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%',
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      fontSize: typography.fontSize.base,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
      color: colors.text,
      outline: 'none',
      boxSizing: 'border-box',
      resize: 'vertical',
      minHeight: 80,
    },
    summaryCard: {
      backgroundColor: colors.backgroundSecondary,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.xl,
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: spacing.sm,
    },
    summaryLabel: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    summaryValue: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      color: colors.text,
    },
    requestButton: {
      width: '100%',
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      backgroundColor: colors.success,
      border: 'none',
      color: colors.white,
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      cursor: 'pointer',
      marginTop: spacing.xl,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.sm,
      transition: 'all 0.2s ease',
    },
    loadingIcon: {
      animation: 'spin 1s linear infinite',
    },
  };

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Request Money"
        subtitle="Request payment from other users"
        showBackButton={true}
        showHomeButton={true}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          <div style={styles.form}>
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Request From</h3>
          <label style={styles.label}>Request Type</label>
          <div style={styles.methodSelector}>
            {requestTypes.map((type) => (
              <button
                key={type.id}
                style={{
                  ...styles.methodButton,
                  ...(requestType === type.id ? styles.activeMethod : {}),
                }}
                onClick={() => setRequestType(type.id)}
              >
                <span style={{ marginRight: spacing.sm }}>{type.icon}</span>
                {type.name}
              </button>
            ))}
          </div>

          <label style={styles.label}>
            {requestTypes.find(t => t.id === requestType)?.name || 'Recipient'}
          </label>
          <input
            type={requestType === 'phone' ? 'tel' : requestType === 'email' ? 'email' : 'text'}
            style={styles.input}
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder={
              requestTypes.find(t => t.id === requestType)?.placeholder || ''
            }
          />
        </div>

        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Request Details</h3>
          <label style={styles.label}>Amount (KES)</label>
          <input
            type="number"
            style={styles.amountInput}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
          />

          <label style={styles.label}>Reason (Optional)</label>
          <textarea
            style={styles.textarea}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why are you requesting this money?"
            rows={3}
          />
        </div>

        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Request Summary</h3>
          <div style={styles.summaryCard}>
            <div style={styles.summaryRow}>
              <span style={styles.summaryLabel}>Request Amount</span>
              <span style={styles.summaryValue}>KES {amount || '0.00'}</span>
            </div>
            <div style={styles.summaryRow}>
              <span style={styles.summaryLabel}>Request Fee</span>
              <span style={styles.summaryValue}>KES 0.00</span>
            </div>
            <div style={{
              ...styles.summaryRow,
              borderTop: `1px solid ${colors.border}`,
              paddingTop: spacing.sm,
              marginTop: spacing.sm,
              marginBottom: 0,
            }}>
              <span style={{
                ...styles.summaryLabel,
                fontWeight: typography.fontWeight.bold,
                color: colors.text,
              }}>
                Total Requested
              </span>
              <span style={{
                ...styles.summaryValue,
                fontSize: typography.fontSize.lg,
                color: colors.success,
              }}>
                {formatCurrency(parseFloat(amount) || 0)}
              </span>
            </div>
          </div>
        </div>

        <button
          style={{
            ...styles.requestButton,
            opacity: loading ? 0.6 : 1,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
          onClick={handleRequest}
          disabled={loading}
        >
          {loading && <span style={styles.loadingIcon}>🔄</span>}
          {loading ? 'Sending Request...' : `Request ${formatCurrency(parseFloat(amount) || 0)}`}
        </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestMoney;